// Scatter overlay — every geocoded signal as a small severity-colored dot.
//
// This is the low-zoom default signal layer (z < 5), replacing the deck.gl
// 9.0.x HeatmapLayer which ships a broken screen-pass shader (see heatmap.ts
// header) and is therefore default-OFF. A ScatterplotLayer has no such bug,
// renders 100k+ points at 60fps on the GPU, and gives the dense "dots on a
// dark map" look of live OSINT monitors (conflictly / worldmonitor) at the
// continental / world zoom the Intel page opens at.
//
// Plots only the "leftover" signal types — those WITHOUT a dedicated icon
// layer (SEI, SAN, CFL, SUP, FIR, NEW each own one). Excluding the
// dedicated-layer types here kills the double-draw the founder hit: an
// earthquake used to render once as an always-on scatter dot AND again as a
// toggleable seismic icon. Now each signal renders exactly once. The
// exclusion set is derived from the registry (`typesWithDedicatedLayer`), so
// it stays correct as icon layers are added. Above z=5 the density-hex + icon
// layers take over (this layer hides), so there's no double-draw with the
// richer mid/high-zoom representations either.

import { ScatterplotLayer } from '@deck.gl/layers';
import type { intel as i } from '@eidollon/contracts';
import { type RGBA, severityRamp } from '../colors.js';
import { withGeom } from './_shared.js';
import type { LayerSpec } from './registry.js';

// Dedicated-layer signal types — each of these has its own toggleable icon
// layer so scatter must not double-render them. Kept here as a static set to
// break the circular registry ↔ scatter import cycle (scatter imports
// LayerSpec from registry; registry imports scatterLayer from scatter).
// If a new dedicated layer is added in registry.ts, add its type code here.
const DEDICATED: ReadonlySet<string> = new Set([
  'CFL', // conflicts
  'SAN', // sanctions
  'SEI', // seismic
  'SUP', // supply chain
  'FIR', // wildfire
  'NEW', // news
]);

/** Keep only rows whose signal_type has no dedicated icon layer. */
function leftoverTypes<T extends i.IntelSignal>(rows: readonly T[]): T[] {
  return withGeom(rows).filter((r) => !DEDICATED.has(r.signal_type));
}

// Severity drives the fill (same ramp as every other severity-axis layer);
// a thin dark stroke keeps dots legible over the bright region choropleth.
const STROKE: RGBA = [8, 12, 20, 200];

export const scatterLayer: LayerSpec = {
  id: 'scatter',
  label: 'PTS · points',
  kind: 'heatmap', // shares the "aggregate, type-less" legend grouping slot
  signalTypes: [],
  color: 'var(--cyan)',
  colorAxis: 'severity',
  description: 'All signals as severity-colored points (low zoom)',
  classification: 'OPEN',
  legend: {
    swatch: 'var(--cyan)',
    label: 'PTS',
    hint: 'Every signal as a point, colored by severity',
  },
  // Default ON — this is the layer that makes the opening (zoomed-out) view
  // show data. Replaces the broken-shader heatmap as the low-zoom default.
  defaultEnabled: true,
  select: (rows) => leftoverTypes(rows),
  // Low zoom only; density-hex (z 5-8) + icons (z≥2/3) own the closer views.
  visibleAtZoom: (z) => z < 5,
  deckLayer: ({ rows, viewport, onPick }) => {
    const data = leftoverTypes(rows);
    return new ScatterplotLayer<i.IntelSignal>({
      id: 'intel-scatter',
      data,
      pickable: Boolean(onPick),
      // biome-ignore lint/style/noNonNullAssertion: withGeom() guarantees geom.
      getPosition: (s) => [s.geom!.lng, s.geom!.lat],
      getFillColor: (s) => severityRamp(s.severity ?? 0),
      getLineColor: STROKE,
      stroked: true,
      lineWidthMinPixels: 0.5,
      // Radius in pixels, gently zoom-scaled so dots stay crisp from world
      // (z0) to the density-hex handoff (z5). radiusMinPixels keeps far-out
      // points visible; radiusMaxPixels caps the near-handoff bloom.
      radiusUnits: 'pixels',
      getRadius: 3,
      radiusMinPixels: 1.8,
      radiusMaxPixels: 5,
      radiusScale: Math.max(1, viewport.zoom * 0.6),
      opacity: 0.9,
      // Always a function (never undefined — exactOptionalPropertyTypes); the
      // optional-chain no-ops when the consumer passed no onPick, and the
      // layer is non-pickable in that case anyway.
      onClick: (info) => onPick?.((info.object as i.IntelSignal) ?? null),
      updateTriggers: { getFillColor: data.length },
    });
  },
};
